"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, Calendar as CalendarIcon } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
export default function JourneyPlanningForm() {
  return <section id="journey-planning-form" className="relative">
      {}
      <div className="absolute inset-0 h-[400px] bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1600&h=800&fit=crop")'
    }}>
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/20"></div>
      </div>

      {}
      <div className="relative z-10 pt-24 md:pt-32 pb-16 md:pb-20">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="mb-16 text-center space-y-4">
            <h2 className="font-default text-3xl md:text-5xl font-light tracking-tight text-white">
              Design Your Experience
            </h2>
            <p className="text-white/90 text-lg max-w-2xl mx-auto font-light">
              Share your vision with us, and our specialists will begin crafting a journey tailored entirely to your preferences.
            </p>
          </div>
        </div>
      </div>

      {}
      <div className="relative z-20 bg-gradient-to-b from-amber-50 via-stone-50 to-amber-50/80 py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <FormProvider formId="plan_journey_form" sectionName="Journey Planning Form" defaultValues={{
          vision: "",
          startDate: undefined,
          endDate: undefined,
          num_seniors: "",
          num_adults: "",
          num_teens: "",
          num_children: "",
          num_infants: "",
          travelStyle: "",
          budget: "",
          fullName: "",
          email: "",
          phone: "",
          referralCode: "",
          additionalDetails: ""
        }} validationRules={{
          vision: {
            required: true,
            minLength: 10
          },
          startDate: {
            required: true
          },
          endDate: {
            required: true
          },
          travelStyle: {
            required: true
          },
          budget: {
            required: true
          },
          fullName: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          referralCode: {
            required: false
          },
          num_seniors: {
            required: false
          },
          num_adults: {
            required: false
          },
          num_teens: {
            required: false
          },
          num_children: {
            required: false
          },
          num_infants: {
            required: false
          }
        }} submitText="Request Your Consultation" successMessage="Thank you for sharing your vision. A specialist will be in touch with you shortly to begin planning your journey.">
            <JourneyFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function JourneyFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200 rounded-xl text-center space-y-6 animate-in fade-in duration-500 shadow-lg">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-amber-900/10 text-amber-900 mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-default text-2xl font-medium text-slate-900">Request Received</h3>
        <p className="text-slate-700 text-lg max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-amber-200 hover:bg-amber-50 text-amber-900">
          Plan Another Journey
        </Button>
      </div>;
  }
  const budgetRanges = [{
    value: "0-500",
    label: "$0 - $500"
  }, {
    value: "500-2500",
    label: "$500 - $2,500"
  }, {
    value: "2500-5000",
    label: "$2,500 - $5,000"
  }, {
    value: "5000-10000",
    label: "$5,000 - $10,000"
  }, {
    value: "10000-20000",
    label: "$10,000 - $20,000"
  }, {
    value: "20000-30000",
    label: "$20,000 - $30,000"
  }, {
    value: "30000-50000",
    label: "$30,000 - $50,000"
  }, {
    value: "50000-75000",
    label: "$50,000 - $75,000"
  }, {
    value: "75000-100000",
    label: "$75,000 - $100,000"
  }, {
    value: "100000-150000",
    label: "$100,000 - $150,000"
  }, {
    value: "150000+",
    label: "$150,000+"
  }];
  const travelStyles = [{
    value: "relaxation",
    label: "Relaxation & Wellness"
  }, {
    value: "adventure",
    label: "Active Adventure"
  }, {
    value: "cultural",
    label: "Cultural Immersion"
  }, {
    value: "luxury",
    label: "Ultra Luxury"
  }, {
    value: "family",
    label: "Family Focused"
  }];
  return <Form onSubmit={handleSubmit}>
      <div className="bg-white rounded-lg shadow-md p-8 md:p-12 space-y-12 border border-amber-100">
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-xl font-medium text-slate-900">1. Your Vision</h3>
            <p className="text-sm text-slate-600">Describe your ideal journey in a few words.</p>
          </div>
          <FormField control={control} name="vision" rules={{
          required: "Please share a brief vision for your trip."
        }} render={({
          field
        }) => <FormItem>
                <FormControl>
                  <Textarea placeholder="e.g., A quiet retreat in the mountains with daily yoga and hiking..." className="min-h-[120px] resize-y text-base focus:ring-amber-700 border-amber-200" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <Separator className="bg-amber-200/40" />

        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-xl font-medium text-slate-900">2. Timing</h3>
            <p className="text-sm text-slate-600">When would you like to travel?</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="startDate" rules={{
            required: "Start date is required"
          }} render={({
            field
          }) => <FormItem className="flex flex-col">
                  <FormLabel className="text-slate-900">Departure</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal h-12 focus:ring-amber-700 border-amber-200 hover:bg-amber-50", !field.value && "text-slate-500")}>
                          {field.value ? new Date(field.value).toLocaleDateString() : <span>Select date</span>}
                          <SafeHydrate><CalendarIcon className="ml-auto h-4 w-4 opacity-50 text-amber-900" /></SafeHydrate>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={field.onChange} disabled={date => date < new Date()} initialFocus /></SafeHydrate>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="endDate" rules={{
            required: "End date is required"
          }} render={({
            field
          }) => <FormItem className="flex flex-col">
                  <FormLabel className="text-slate-900">Return</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal h-12 focus:ring-amber-700 border-amber-200 hover:bg-amber-50", !field.value && "text-slate-500")}>
                          {field.value ? new Date(field.value).toLocaleDateString() : <span>Select date</span>}
                          <SafeHydrate><CalendarIcon className="ml-auto h-4 w-4 opacity-50 text-amber-900" /></SafeHydrate>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={field.onChange} disabled={date => date < new Date()} initialFocus /></SafeHydrate>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <Separator className="bg-amber-200/40" />

        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-xl font-medium text-slate-900">3. The Details</h3>
            <p className="text-sm text-slate-600">Who is traveling and what is your preferred style?</p>
          </div>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <FormField control={control} name="num_seniors" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-slate-900">Seniors</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" placeholder="65+" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="num_adults" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-slate-900">Adults</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" placeholder="18-64" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="num_teens" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-slate-900">Teens</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" placeholder="12-17" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="num_children" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-slate-900">Children</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" placeholder="2-11" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="num_infants" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-slate-900">Infants</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" placeholder="0-2" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="max-w-md">
              <FormField control={control} name="travelStyle" rules={{
              required: "Required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-slate-900">Travel Style</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 focus:ring-amber-700 border-amber-200">
                          <SelectValue placeholder="Select style" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {travelStyles.map(style => <SelectItem key={style.value} value={style.value}>
                            {style.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </div>

        <Separator className="bg-amber-200/40" />

        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-xl font-medium text-slate-900">4. Estimated Budget</h3>
            <p className="text-sm text-slate-600">Anticipated budget for this journey (excluding international flights).</p>
          </div>
          <div className="max-w-md">
            <FormField control={control} name="budget" rules={{
            required: "Please select an anticipated budget range."
          }} render={({
            field
          }) => <FormItem>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-12 focus:ring-amber-700 border-amber-200">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {budgetRanges.map(range => <SelectItem key={range.value} value={range.value}>
                          {range.label}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <Separator className="bg-amber-200/40" />

        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-xl font-medium text-slate-900">5. Contact Information</h3>
            <p className="text-sm text-slate-600">How should we reach you to discuss your plans?</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="fullName" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-slate-900">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Doe" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-slate-900">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            <FormField control={control} name="phone" render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel className="text-slate-900">Phone Number (Optional)</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 000-0000" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <Separator className="bg-amber-200/40" />

        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-xl font-medium text-slate-900">6. Referral & VIP Code</h3>
            <p className="text-sm text-slate-600">Were you referred by a friend or do you have a VIP access code?</p>
          </div>
          <FormField control={control} name="referralCode" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-slate-900">Referred By / VIP Code (Optional)</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="e.g., John Smith or VIPCODE123" className="h-12 focus:ring-amber-700 border-amber-200" {...field} />
                </FormControl>
                <FormDescription>
                  If you were invited by a current CircuitX member, please enter their name or code.
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <Separator className="bg-amber-200/40" />

        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-xl font-medium text-slate-900">7. Additional Details</h3>
            <p className="text-sm text-slate-600">Any special occasions, dietary requirements, or specific must-haves?</p>
          </div>
          <FormField control={control} name="additionalDetails" render={({
          field
        }) => <FormItem>
                <FormControl>
                  <Textarea placeholder="Optional notes..." className="min-h-[100px] resize-y focus:ring-amber-700 border-amber-200" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {submitError}
          </div>}

        <div className="pt-6 flex justify-end">
          <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto h-14 px-8 text-base font-medium bg-amber-900 hover:bg-amber-950 text-white shadow-md hover:shadow-lg transition-all">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 h-5 w-5" />
                Processing Request...
              </> : "Request Your Consultation"}
          </Button>
        </div>
      </div>
    </Form>;
}